"use client";

import { useNow as useRenderInstant } from "next-intl";
import { useSyncExternalStore } from "react";

const TICK_MS = 60_000;

const listeners = new Set<() => void>();
let tickMs = 0;
let timer: ReturnType<typeof setInterval> | undefined;

function subscribe(onTick: () => void) {
  listeners.add(onTick);
  if (!timer) {
    timer = setInterval(() => {
      tickMs = Date.now();
      for (const listener of listeners) listener();
    }, TICK_MS);
  }
  return () => {
    listeners.delete(onTick);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = undefined;
      tickMs = 0;
    }
  };
}

function readClock() {
  if (!tickMs) tickMs = Date.now();
  return tickMs;
}

/**
 * The current instant for clock-derived UI on a page whose HTML may be cached.
 *
 * Hydration uses the instant the server rendered at (next-intl's `now`), so it
 * matches the possibly stale HTML. The browser then follows the real clock,
 * shared across every mounted caller and refreshed once a minute.
 */
export function useNow(): Date {
  const renderInstant = useRenderInstant();
  const ms = useSyncExternalStore(subscribe, readClock, () =>
    renderInstant.getTime(),
  );
  return new Date(ms);
}
